/* eslint-disable no-unused-vars */
import { Delete } from '@mui/icons-material'
import { Box, CircularProgress, Divider, Grid, IconButton, Typography } from '@mui/material'
import { clearString } from '~/utils'
import { brazilStates } from '~/constants'
import { Input } from '~/components/Form/Input'
import { Button } from '~/components/Form/Button'
import { Select } from '~/components/Form/Select'
import { maritalStatusOptions } from '~/utils/options'
import { DragAndDrop } from '~/components/Form/DragAndDrop'
import { CustomerFileModel, CustomerModel, DocumentationAnalysisFileModel, ECustomerFileType, EProposalFileType } from '~/graphql/types'
import { LegalRepresentativeType } from '../../utils'
import { useCallback } from 'react'
import { useFormContext } from 'react-hook-form'
import { useCep } from '~/hooks'
import { IconSearch } from '@tabler/icons-react'
import { useContract } from '~/contexts'

export type LegalRepresentativeFieldsProps = {
  index: number
  loading: boolean
  fieldsLength: number
  disableFields?: boolean
  customer?: CustomerModel | null
  legalRepresentative: LegalRepresentativeType
  removeLegalRepresentative: (index: number) => void
}

const fieldName = 'legalRepresentatives'

export const LegalRepresentativeFields: React.FC<LegalRepresentativeFieldsProps> = ({ disableFields, index, removeLegalRepresentative, fieldsLength, loading, legalRepresentative, customer }) => {
  const { setValue } = useFormContext()
  const { fetchCep, isLoading: fetchCepIsLoading } = useCep({ showErrorMessage: true })
  const { contract } = useContract()
  const currentLegalRepresentative = customer?.legalRepresentatives?.find(representative => clearString(representative.document || '') === clearString(legalRepresentative.document))

  const isFormalized = Boolean(contract?.isFormalizedByPartner)

  // Buscar o representante legal específico no contrato usando o índice
  const contractLegalRepresentative = contract?.customerRef?.legalRepresentatives?.[index]

  // Documentos conforme origem
  const frontDocument = isFormalized
    ? contractLegalRepresentative?.files?.find(file => file.type === EProposalFileType.customerPjRepresentativeDocumentWithPhotoFront)
    : currentLegalRepresentative?.files?.find(file => file.type === ECustomerFileType.customerPjRepresentativeDocumentWithPhotoFront)

  const backDocument = isFormalized
    ? contractLegalRepresentative?.files?.find(file => file.type === EProposalFileType.customerPjRepresentativeDocumentWithPhotoBack)
    : currentLegalRepresentative?.files?.find(file => file.type === ECustomerFileType.customerPjRepresentativeDocumentWithPhotoBack)

  const onSearchCep = useCallback(async () => {
    if (legalRepresentative && legalRepresentative?.address?.zipcode) {
      const { data, isSuccess } = await fetchCep(legalRepresentative.address.zipcode)
      if (isSuccess) {
        setValue(`${fieldName}.${index}.address.state`, data?.uf || '')
        setValue(`${fieldName}.${index}.address.city`, data?.localidade || '')
        setValue(`${fieldName}.${index}.address.neighborhood`, data?.bairro || '')
        setValue(`${fieldName}.${index}.address.street`, data?.logradouro || '')
      }
    }
  }, [legalRepresentative, index])

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Typography variant='h3' fontWeight={500}>Representante legal</Typography>
      <Grid spacing={3} container>
        <Grid item md={8} xs={12}>
          <Input disabled={disableFields} name={`${fieldName}.${index}.name`} label='Nome completo' />
        </Grid>
        <Grid item md={4} xs={12}>
          <Input disabled={disableFields} mask='cpf' name={`${fieldName}.${index}.document`} label='CPF' />
        </Grid>

        <Grid item md={4} xs={12}>
          <Input disabled={disableFields} name={`${fieldName}.${index}.identificationNumber`} label='RG' />
        </Grid>
        <Grid item md={4} xs={12}>
          <Input disabled={disableFields} name={`${fieldName}.${index}.email`} label='E-mail' />
        </Grid>
        <Grid item md={4} xs={12}>
          <Input disabled={disableFields} mask='phone' name={`${fieldName}.${index}.phone`} label='Telefone' />
        </Grid>

        <Grid item md={4} xs={12}>
          <Input disabled={disableFields} name={`${fieldName}.${index}.nationality`} label='Nacionalidade' />
        </Grid>
        <Grid item md={4} xs={12}>
          <Select disabled={disableFields} name={`${fieldName}.${index}.maritalStatus`} label='Estado civil' options={maritalStatusOptions} />
        </Grid>
      </Grid>

      <Typography fontWeight={500}>Endereço</Typography>
      <Grid spacing={3} container>
        <Grid item md={4} xs={12}>
          <Input
            disabled={disableFields}
            icons={{ end: { element: fetchCepIsLoading ? <CircularProgress size={18} color='inherit' /> : <IconButton disabled={disableFields} onClick={() => onSearchCep()}><IconSearch /></IconButton> } }}
            label='CEP'
            name={`${fieldName}.${index}.address.zipcode`}
            mask='cep'
          />
        </Grid>
        <Grid item md={8} xs={12} />

        <Grid item md={4} xs={12}>
          <Input disabled={disableFields} label='Rua' name={`${fieldName}.${index}.address.street`} />
        </Grid>
        <Grid item md={4} xs={12}>
          <Input disabled={disableFields} label='Número' name={`${fieldName}.${index}.address.number`} />
        </Grid>
        <Grid item md={4} xs={12}>
          <Input disabled={disableFields} label='Complemento (Opcional)' name={`${fieldName}.${index}.address.complement`} />
        </Grid>

        <Grid item md={4} xs={12}>
          <Input disabled={disableFields} label='Bairro' name={`${fieldName}.${index}.address.neighborhood`} />
        </Grid>
        <Grid item md={4} xs={12}>
          <Input disabled={disableFields} label='Cidade' name={`${fieldName}.${index}.address.city`} />
        </Grid>
        <Grid item md={4} xs={12}>
          <Select disabled={disableFields} name={`${fieldName}.${index}.address.state`} label='UF' options={brazilStates} />
        </Grid>
      </Grid>

      <Typography fontWeight={500}>Documento</Typography>
      <Grid spacing={3} container>
        <Grid item md={6} xs={12}>
          <DragAndDrop
            disabled={disableFields}
            label='Anexar RG ou CNH (Frente)'
            fileDescription='(JPG, JPEG, PNG, PDF. Tamanho máximo 15MB)'
            $variant='secondary'
            sentAt={isFormalized ? (frontDocument as any)?.sentAt : (frontDocument as any)?.sendAt}
            $completed={Boolean(isFormalized ? (frontDocument as any)?.sentAt : (frontDocument as any)?.sendAt)}
            fileName={frontDocument?.file?.fileName}
            fileUrl={frontDocument?.file?.key ? `${process.env.VITE_APP_REST_API_URL}files/download/file/${frontDocument?.file?.key}` : frontDocument?.file?.fileUrl}
            fileKey={frontDocument?.file?.key}
            status={isFormalized ? (frontDocument as any)?.status : null}
            name={`${fieldName}.${index}.customerPjRepresentativeDocumentWithPhotoFront`}
            accepted={{
              images: true,
              pdf: true
            }}
          />
        </Grid>
        <Grid item md={6} xs={12}>
          <DragAndDrop
            disabled={disableFields}
            $variant='secondary'
            label='Anexar RG ou CNH (Verso)'
            fileDescription='(JPG, JPEG, PNG, PDF. Tamanho máximo 15MB)'
            sentAt={isFormalized ? (backDocument as DocumentationAnalysisFileModel)?.sentAt : (backDocument as CustomerFileModel)?.sendAt}
            $completed={Boolean(isFormalized ? (backDocument as DocumentationAnalysisFileModel)?.sentAt : (backDocument as CustomerFileModel)?.sendAt)}
            fileName={backDocument?.file?.fileName}
            fileUrl={backDocument?.file?.key ? `${process.env.VITE_APP_REST_API_URL}files/download/file/${backDocument?.file?.key}` : backDocument?.file?.fileUrl}
            fileKey={backDocument?.file?.key}
            status={isFormalized ? (backDocument as DocumentationAnalysisFileModel)?.status : null}
            name={`${fieldName}.${index}.customerPjRepresentativeDocumentWithPhotoBack`}
            accepted={{
              images: true,
              pdf: true
            }}
          />
        </Grid>
      </Grid>

      {fieldsLength > 1 && (
        <Button
          color='error'
          disabled={loading}
          startIcon={<Delete />}
          style={{ alignSelf: 'end' }}
          variant='outlined'
          onClick={() => removeLegalRepresentative(index)}
        >
          Remover representante
        </Button>
      )}

      {!(fieldsLength === index + 1) && <Divider />}
    </Box>
  )
}
